import React, { useEffect, useState } from "react";
import StatCard from "../../../components/card/StatCard";
import ForecastSummary from "./components/ForecastSummary";
import DashboardEfficiencyGraph from "./components/DashboardEfficiencyGraph";
import DashboardSalesGraph from "./components/DashboardSalesGraph";
import { Card } from "../../../components/ui/card";
import axios from "axios";
import API_BASE_URL from "config/api";
import { useInterval } from "contexts/IntervalContext";
import { fetchDashboardActions } from "api/dashboardActions";
import { ArrowUpRight, ArrowDownRight } from "lucide-react";
import PriorityActionCard from "./components/PriorityActionCard";



export default function AdminDefault() {
  const { interval } = useInterval();
  const [dashboard, setDashboard] = useState(null);
  const [actions, setActions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const demandStatus = dashboard?.forecast_metrics?.demand_status;

  const demandBadge =
    demandStatus === "high"
      ? { text: "HIGH", tone: "positive", icon: <ArrowUpRight className="w-3 h-3" /> }
      : demandStatus === "low"
      ? { text: "LOW", tone: "negative", icon: <ArrowDownRight className="w-3 h-3" /> }
      : { text: "STABLE", tone: "neutral", icon: null };

  /* =========================
     Fetch Dashboard
  ========================= */
  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        setIsLoading(true);
        const token = localStorage.getItem("access_token");
        const res = await axios.get(
          `${API_BASE_URL}/api/products/dashboard/forecast/?interval=${interval}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setDashboard(res.data);
        console.log("dashboard data",res.data)
      } catch (err) {
        console.error("Failed to fetch dashboard forecast:", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchDashboard();
  }, [interval]);

  /* =========================
     Fetch Actions
  ========================= */
  useEffect(() => {
    fetchDashboardActions(interval).then(setActions).catch(console.error);
  }, [interval]);

  const fmtCurrency = (v) =>
    v == null ? "—" : `$${Number(v).toLocaleString()}`;

  const fmtPercent = (v) =>
    v == null ? "—" : `${v > 0 ? "+" : ""}${Number(v).toFixed(1)}%`;

  return (
    <div className="min-h-screen bg-neutral-950 text-gray-100 pt-[40px] px-4 lg:px-6">

      {/* ================= KPI ROW ================= */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard
          title={`Forecast Revenue (${interval})`}
          value={fmtCurrency(dashboard?.forecast_metrics?.total_predicted_revenue)}
          change={fmtPercent(dashboard?.forecast_metrics?.forecast_growth_rate)}
          trend="Projected total revenue"
          description="Across all products"
        />

        <StatCard
          title="Demand"
          value={demandStatus?.toUpperCase() ?? "—"}
          badge={demandBadge}
          trend={
            dashboard?.forecast_metrics?.top_demand_driver
              ? `Driven by ${dashboard.forecast_metrics.top_demand_driver.product_name}`
              : "No dominant driver"
          }
          description={
            dashboard?.forecast_metrics?.demand_index
              ? `Index ${dashboard.forecast_metrics.demand_index} (baseline = 100)`
              : "Baseline demand"
          }
        />

        <StatCard
          title="Expected Profit"
          value={fmtCurrency(dashboard?.forecast_metrics?.total_gross_profit)}
          trend={`Margin ${dashboard?.forecast_metrics?.margin_status?.toUpperCase()}`}
          description={`Gross margin ${dashboard?.forecast_metrics?.gross_margin}%`}
        />

        <StatCard
          title="Inventory Risk"
          value={dashboard?.forecast_metrics?.recommended_restock_qty ?? "—"}
          trend={
            dashboard?.forecast_metrics?.stockout_risk === "high"
              ? "⚠ Many items close to stockout"
              : "Inventory under control"
          }
          description="Based on remaining stock vs demand"
        />
      </div>

      {/* ================= PRIORITY ACTION ================= */}
      <div className="mb-6">
        <PriorityActionCard
          action={actions?.[0]}
          interval={interval}
          onClick={() => {
            window.location.href = "/dashboard/actions";
          }}
        />
      </div>

      {/* ================= GRAPHS + SUMMARY ================= */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

        {/* 📊 LEFT: GRAPHS */}
        <div className="lg:col-span-2 space-y-3">

          <Card className="p-3 bg-muted/30 border border-border/60 rounded-2xl">
            <h3 className="text-xs font-medium mb-1 text-gray-400">
              Predicted vs Actual Sales
            </h3>
            <DashboardSalesGraph
              interval={interval}
              salesData={dashboard}
              isLoading={isLoading}
              height={150}
            />
          </Card>

          <Card className="p-3 bg-muted/30 border border-border/60 rounded-2xl">
            <DashboardEfficiencyGraph
              salesData={dashboard}
              isLoading={isLoading}
              interval={interval}
              compact
            />
          </Card>

        </div>

        {/* 📝 RIGHT: SUMMARY */}
        <div className="lg:col-span-3">
          <ForecastSummary
            summary={dashboard?.forecast_summary?.summary_text}
            growthRate={dashboard?.forecast_summary?.growth_summary}
            topDrivers={dashboard?.forecast_summary?.key_drivers ?? []}
            actionPlan={dashboard?.forecast_summary?.action_plan}
            inventoryNotes={dashboard?.forecast_summary?.inventory_notes}
            isLoading={isLoading}
          />
        </div>

      </div>
    </div>
  );
}